/**
 * RhPage — módulo RH "pessoal e custo" (#29 F1b, reproduz docs/mocks/rh_mock.html).
 *
 * PII: a tela consome SÓ os agregados do F1a (/api/rh/*) — NUNCA mostra
 * indivíduo (nome/salário de pessoa não trafega).
 *
 * Seções com DADO REAL (endpoints F1a):
 *   - KPIs: Headcount (+Δ vs mês anterior, da /evolucao), Custo de pessoal
 *     (card EXPANSÍVEL #29-polish: disclosure com as 7 linhas da composição),
 *     Custo médio/pessoa (/headcount + /custo).
 *   - Custo operacional por área EX-SÓCIOS (#29-polish): TODAS as áreas,
 *     área a área desc, sem colapso "+N áreas" (/headcount campos ex_socios).
 *   - Evolução headcount (/evolucao) + evolução do CUSTO (#29-polish):
 *     empilhado Salário/Benefícios/Encargos com os 7 no tooltip; dropdown
 *     destaca a série de um componente; série vazia → "aguardando".
 *
 * Seções PLACEHOLDER (dado é F2 — doutrina #246: nada de mock que parece
 * pronto, nada de botão morto): Reconciliação 3-fontes (RH↔DRE↔Omie) e
 * Férias/Horas extras — renderizam o LAYOUT do mock com "—" e badge
 * discreto "em breve", zero número falso, zero botão morto (o único botão
 * da página é o disclosure do custo, que faz coisa de verdade).
 */
import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Loading } from "../components/Loading";
import { KpiCard } from "../components/ui/KpiCard";
import { useRh } from "../hooks/useRh";
import { fmtBRL } from "../utils/formatters";

/** Ordem/labels das 7 componentes do custo (chaves == API/colunas). */
const COMPONENTES: Array<{ key: string; label: string; grupo: "salario" | "beneficios" | "encargos" }> = [
  { key: "salario", label: "Salário", grupo: "salario" },
  { key: "convenio_medico", label: "Convênio médico", grupo: "beneficios" },
  { key: "odontologico", label: "Odontológico", grupo: "beneficios" },
  { key: "seguro_vida", label: "Seguro de vida", grupo: "beneficios" },
  { key: "totalpass", label: "Wellness (TotalPass)", grupo: "beneficios" },
  { key: "vale_transporte", label: "Vale transporte", grupo: "beneficios" },
  { key: "encargos_clt", label: "Encargos CLT", grupo: "encargos" },
];

const MESES_LONGO = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
const MESES_CURTO = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez",
];

function mesLongo(mesRef: string): string {
  const [y, m] = mesRef.split("-").map(Number);
  return `${MESES_LONGO[m - 1]} ${y}`;
}

function mesCurto(mesRef: string): string {
  return MESES_CURTO[Number(mesRef.split("-")[1]) - 1];
}

/** Badge "em breve" dos cards F2 — discreto, não parece dado pronto (#246). */
function EmBreve() {
  return (
    <span
      className="text-[10px] uppercase tracking-wider text-white/40 border border-white/15 rounded px-1.5 py-0.5"
      data-testid="rh-em-breve"
    >
      em breve
    </span>
  );
}

/** Linha k/v dos cards placeholder — valor sempre "—" (sem número falso). */
function StRow({ k }: { k: string }) {
  return (
    <div className="flex justify-between text-sm py-2 border-b border-white/10 last:border-b-0">
      <span className="text-slate-300/80">{k}</span>
      <span className="text-white/30">—</span>
    </div>
  );
}

export function RhPage() {
  const { data, isLoading, error } = useRh();
  // #29-polish: disclosure do KPI de custo + componente destacado na evolução.
  const [custoAberto, setCustoAberto] = useState(false);
  const [compSel, setCompSel] = useState<string>("empilhado");
  // #29-vinculo: visão dos KPIs — Total (incl sócios/saindo) × Ex-sócios (ativos).
  const [visao, setVisao] = useState<"total" | "ex_socios">("total");

  const hc = data?.headcount ?? null;
  const custo = data?.custo ?? null;
  const serie = data?.evolucao.serie ?? [];
  const status = hc?.status ?? null;
  const exAtivos = custo?.ex_socios_ativos ?? null;
  const temStatus = status !== null && (status.ativos + status.saindo + status.freela) > 0;

  // (#29-vinculo: o Δ vs mês anterior saiu do KPI — a linha secundária agora
  // é o status "+N saindo · M freela"; a tendência vive na seção Evolução.)

  // #29-polish: custo OPERACIONAL por área = ex-sócios, TODAS as áreas desc.
  const areas = useMemo(() => {
    const deps = (hc?.departamentos ?? [])
      .filter((d) => d.n_ex_socios > 0)
      .sort((a, b) => b.custo_ex_socios - a.custo_ex_socios);
    const maxCusto = Math.max(1, ...deps.map((d) => d.custo_ex_socios));
    return { deps, maxCusto };
  }, [hc]);

  // 7 linhas da composição do mês vigente (disclosure do KPI de custo).
  const composicaoMes = useMemo(() => {
    if (!custo) return [];
    const det = custo.composicao.beneficios_detalhe ?? {};
    const valores: Record<string, number> = {
      salario: custo.composicao.salario ?? 0,
      encargos_clt: custo.composicao.encargos_clt ?? 0,
      ...det,
    };
    const soma = Object.values(valores).reduce((s, v) => s + v, 0);
    return COMPONENTES.map((c) => ({
      ...c,
      valor: valores[c.key] ?? 0,
      share: soma > 0 ? ((valores[c.key] ?? 0) / soma) * 100 : 0,
    }));
  }, [custo]);
  const somaComponentesMes = composicaoMes.reduce((s, c) => s + c.valor, 0);
  const difFonteMes = custo ? Math.round((custo.total - somaComponentesMes) * 100) / 100 : 0;

  // Série da evolução do CUSTO (empilhado por grupo ou componente destacado).
  const evoCusto = useMemo(() => {
    const pontos = serie.map((p) => {
      const comp = p.composicao ?? {};
      const grupos = { salario: 0, beneficios: 0, encargos: 0 };
      for (const c of COMPONENTES) grupos[c.grupo] += comp[c.key] ?? 0;
      const totalComp = grupos.salario + grupos.beneficios + grupos.encargos;
      return { mes_ref: p.mes_ref, comp, grupos, totalComp, dif: p.dif_componentes ?? 0 };
    });
    const max = Math.max(
      1,
      ...pontos.map((p) => (compSel === "empilhado" ? p.totalComp : p.comp[compSel] ?? 0)),
    );
    // Furo DA FONTE (review item 3): meses onde a soma dos componentes
    // difere do total (linhas sem Total Individual na planilha).
    const mesesComDif = pontos.filter((p) => Math.abs(p.dif) > 0.01);
    return { pontos, max, mesesComDif };
  }, [serie, compSel]);

  const maxHcSerie = Math.max(1, ...serie.map((p) => p.headcount));
  const mesAtual = serie.length ? serie[serie.length - 1].mes_ref : hc?.mes_ref ?? null;

  // #29-vinculo: números dos KPIs por visão. Headcount = ATIVOS (qtdd=1);
  // saindo/freela viram linha secundária (fora do número principal). Sem
  // status (pré-backfill v2) → fallback honesto pro total de linhas.
  const exVisao = visao === "ex_socios";
  const kpiHeadcount = temStatus
    ? (exVisao ? status!.ativos_ex_socios : status!.ativos)
    : hc?.total.n ?? 0;
  const kpiCusto = exVisao ? (exAtivos?.total ?? null) : (hc?.total.custo ?? null);
  const kpiMedio = exVisao
    ? (exAtivos?.medio ?? null)
    : hc && hc.total.n > 0 ? hc.total.custo / hc.total.n : null;
  const subHeadcount = temStatus
    ? `+${status!.saindo} saindo · ${status!.freela} freela`
    : "sem status (qtdd) — re-ingest pendente";

  return (
    <div data-testid="rh-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-white font-sans">RH — pessoal e custo</h1>
          <p className="text-white/50 text-sm mt-1">
            Fonte: controle do RH · reconciliação com DRE e Omie na próxima fase.
          </p>
        </div>
        <span
          className="text-sm text-slate-300/90 border border-white/10 rounded-lg px-3 py-1.5 whitespace-nowrap"
          data-testid="rh-chip-mes"
        >
          {hc?.mes_ref ? mesLongo(hc.mes_ref) : "sem dados"}
        </span>
      </div>

      {error ? (
        <div
          className="rounded-xl border border-indra-negativo/30 bg-indra-negativo/10 p-6 text-sm text-white/80"
          data-testid="rh-error"
        >
          Não foi possível carregar o RH. Verifique a conexão e tente novamente.
        </div>
      ) : isLoading || !data ? (
        <div className="flex items-center justify-center py-20" data-testid="rh-loading">
          <Loading label="Carregando RH…" />
        </div>
      ) : (
        <>
          {/* #29-vinculo: toggle da visão dos KPIs (Total × Equipe = ativos sem
              sócios) — rotulo Veiga 06/07; campos da API seguem internos (ex_socios). */}
          <div className="flex items-center gap-2 mb-3" data-testid="rh-visao-toggle">
            {([["total", "Total"], ["ex_socios", "Equipe"]] as const).map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setVisao(v)}
                aria-pressed={visao === v}
                data-testid={`rh-visao-${v}`}
                className={
                  "text-xs rounded-lg px-3 py-1.5 border transition-colors " +
                  (visao === v
                    ? "border-indra-turquesa/50 text-indra-turquesa bg-indra-turquesa/10"
                    : "border-white/10 text-white/60 hover:text-white/80")
                }
              >
                {label}
              </button>
            ))}
          </div>

          {/* KPIs — dado real (/headcount + /custo); headcount = ATIVOS (qtdd=1) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-stretch" data-testid="rh-kpis">
            <KpiCard
              label={exVisao ? "Headcount — equipe" : "Headcount ativo"}
              value={kpiHeadcount > 0 ? String(kpiHeadcount) : "—"}
              sub={subHeadcount}
              subTone="muted"
              highlight
              testId="rh-kpi-headcount"
            />
            {/* #29-polish: card expansível — disclosure REAL (aria-expanded),
                aberto mostra as 7 linhas da composição do mês vigente. */}
            <div
              className="rounded-xl border border-white/10 bg-indra-azul-surface h-full flex flex-col"
              data-testid="rh-kpi-custo"
            >
              <button
                type="button"
                onClick={() => setCustoAberto((v) => !v)}
                aria-expanded={custoAberto}
                data-testid="rh-kpi-custo-toggle"
                className="w-full text-left p-5 flex flex-col gap-2 rounded-xl hover:bg-white/5 active:bg-white/10"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] tracking-wider text-white/60">
                    {exVisao ? "Custo de pessoal — equipe" : "Custo de pessoal"}
                  </span>
                  <span className="text-white/40">
                    {custoAberto ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </span>
                </div>
                <div
                  className="text-2xl font-black text-white font-sans whitespace-nowrap tabular-nums"
                  data-testid="rh-kpi-custo-value"
                >
                  {kpiCusto !== null && kpiCusto > 0 ? fmtBRL(kpiCusto, { abbreviate: true }) : "—"}
                </div>
                <div className="text-xs text-white/60">
                  {custoAberto ? "composição do mês abaixo" : "toque para abrir a composição"}
                </div>
              </button>
              {custoAberto && (
                <div className="px-5 pb-4 -mt-1" data-testid="rh-custo-composicao">
                  {exVisao && (
                    <div className="text-[11px] text-white/40 pb-1.5">
                      composição abaixo = mês TOTAL (por status é F2)
                    </div>
                  )}
                  {composicaoMes.map((c) => (
                    <div
                      key={c.key}
                      className="flex justify-between text-xs py-1.5 border-b border-white/10 last:border-b-0"
                    >
                      <span className="text-slate-300/80">{c.label}</span>
                      <span className="font-mono tabular-nums text-white">
                        {fmtBRL(c.valor, { decimals: 0 })}
                        <span className="text-white/50 ml-1.5">
                          {c.share.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
                        </span>
                      </span>
                    </div>
                  ))}
                  {Math.abs(difFonteMes) > 0.01 && (
                    <div className="text-[11px] text-amber-300/80 pt-2" data-testid="rh-custo-dif-fonte">
                      Total da fonte difere em {fmtBRL(difFonteMes, { decimals: 0 })} — linha(s)
                      sem Total Individual na planilha (em conferência).
                    </div>
                  )}
                </div>
              )}
            </div>
            <KpiCard
              label={exVisao ? "Custo médio/pessoa — equipe" : "Custo médio/pessoa"}
              value={kpiMedio !== null ? fmtBRL(kpiMedio, { abbreviate: true }) : "—"}
              sub={exVisao ? "custo da equipe ÷ ativos da equipe" : "custo total ÷ pessoas no mês"}
              subTone="muted"
              testId="rh-kpi-medio"
            />
          </div>

          {/* Reconciliação 3-fontes — PLACEHOLDER F2 (layout do mock, sem dado falso) */}
          <div className="bg-indra-azul-surface rounded-xl border border-white/10 p-5 mb-6" data-testid="rh-reconciliacao">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="text-white font-semibold text-sm">
                Custo de pessoal — três fontes, uma verdade
              </h3>
              <EmBreve />
            </div>
            <p className="text-xs text-white/60 mb-4">
              O RH projeta a folha; o Córtex bate com o DRE (competência) e o Omie (pago).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { k: "Folha RH", u: "esperado" },
                { k: "DRE pessoal", u: "competência" },
                { k: "Omie pago", u: "caixa" },
              ].map((f) => (
                <div key={f.k} className="border border-white/10 rounded-lg px-4 py-3">
                  <div className="text-xs text-slate-300/80">{f.k}</div>
                  <div className="text-lg font-semibold text-white/30 mt-1">—</div>
                  <div className="text-[11px] text-white/40">{f.u}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10 text-xs text-white/40">
              <span>Δ a conciliar aparece aqui</span>
              <span>→ projeta a saída de pessoal no Fluxo de Caixa</span>
            </div>
          </div>

          {/* Férias + Horas extras — PLACEHOLDER F2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-indra-azul-surface rounded-xl border border-white/10 p-5" data-testid="rh-ferias">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-white font-semibold text-sm">Férias</h3>
                <EmBreve />
              </div>
              <p className="text-xs text-white/60 mb-2">Provisão e vencimentos</p>
              <StRow k="Provisão acumulada" />
              <StRow k="Vencendo em ≤60 dias" />
              <StRow k="Dias médios acumulados" />
            </div>
            <div className="bg-indra-azul-surface rounded-xl border border-white/10 p-5" data-testid="rh-horas-extras">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-white font-semibold text-sm">Horas extras</h3>
                <EmBreve />
              </div>
              <p className="text-xs text-white/60 mb-2">No mês e banco de horas</p>
              <StRow k="Custo no mês" />
              <StRow k="Maior área" />
              <StRow k="Banco de horas (saldo)" />
            </div>
          </div>

          {/* Custo por área — equipe, TODAS as áreas (#29-polish) */}
          <h3 className="text-white font-semibold text-sm mb-1">
            Custo por área — equipe
          </h3>
          <p className="text-xs text-white/60 mb-3">
            Equipe = sem os sócios (inclui quem está saindo no mês). Todas as áreas, maior → menor.
          </p>
          <div className="mb-6" data-testid="rh-custo-area">
            {areas.deps.length === 0 ? (
              <div className="text-white/60 text-sm" data-testid="rh-custo-area-vazio">
                Sem dados de headcount no mês.
              </div>
            ) : (
              areas.deps.map((d) => (
                <div key={d.departamento} className="flex items-center gap-3 mb-2.5" data-testid={`rh-area-${d.departamento}`}>
                  <div className="w-36 text-right text-sm text-slate-300/90 truncate flex-shrink-0">
                    {d.departamento} <span className="text-white/40">· {d.n_ex_socios}</span>
                  </div>
                  <div className="flex-1 bg-indra-azul-deep/60 rounded h-5 overflow-hidden">
                    <div
                      className="h-full bg-indra-turquesa rounded"
                      style={{ width: `${(d.custo_ex_socios / areas.maxCusto) * 100}%` }}
                    />
                  </div>
                  <div className="w-24 text-sm font-mono tabular-nums text-white flex-shrink-0">
                    {fmtBRL(d.custo_ex_socios, { abbreviate: true })}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Evolução headcount — dado real (/evolucao); vazio = honesto */}
          <h3 className="text-white font-semibold text-sm mb-3">Evolução — headcount</h3>
          {serie.length === 0 ? (
            <div
              className="bg-indra-azul-surface rounded-xl border border-white/10 p-6 text-sm text-white/60"
              data-testid="rh-evolucao-vazia"
            >
              Aguardando dados históricos — a série mensal aparece aqui após o
              backfill dos meses anteriores.
            </div>
          ) : (
            <>
              <div className="flex items-end gap-3 h-32 mb-6" data-testid="rh-evolucao">
                {serie.map((p) => {
                  const atual = p.mes_ref === mesAtual;
                  return (
                    <div key={p.mes_ref} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
                      <div className="w-full flex items-end" style={{ height: "5.5rem" }}>
                        <div
                          className={"w-full rounded-t " + (atual ? "bg-indra-turquesa" : "bg-slate-500/70")}
                          style={{ height: `${Math.max(6, (p.headcount / maxHcSerie) * 100)}%` }}
                          title={`${mesCurto(p.mes_ref)}: ${p.headcount} pessoas`}
                        />
                      </div>
                      <span className={"text-xs " + (atual ? "text-white font-semibold" : "text-white/50")}>
                        {mesCurto(p.mes_ref)} · {p.headcount}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Evolução do CUSTO (#29-polish): empilhado por grupo; dropdown
                  destaca a série de UM componente (os 7 sempre no tooltip). */}
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <h3 className="text-white font-semibold text-sm">Evolução — custo de pessoal</h3>
                <select
                  value={compSel}
                  onChange={(e) => setCompSel(e.target.value)}
                  aria-label="Componente do custo"
                  data-testid="rh-evocusto-select"
                  className="bg-indra-azul-deep border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white/80"
                >
                  <option value="empilhado">Empilhado (salário · benefícios · encargos)</option>
                  {COMPONENTES.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-3 h-36" data-testid="rh-evocusto">
                {evoCusto.pontos.map((p) => {
                  const atual = p.mes_ref === mesAtual;
                  const tooltip = COMPONENTES
                    .map((c) => `${c.label}: ${fmtBRL(p.comp[c.key] ?? 0, { decimals: 0 })}`)
                    .join(" · ")
                    + (Math.abs(p.dif) > 0.01
                      ? ` · Total da fonte difere: ${fmtBRL(p.dif, { decimals: 0 })}`
                      : "");
                  const valorLabel = compSel === "empilhado" ? p.totalComp : p.comp[compSel] ?? 0;
                  return (
                    <div key={p.mes_ref} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
                      <span className="text-[11px] font-mono text-white/70 whitespace-nowrap">
                        {fmtBRL(valorLabel, { abbreviate: true })}
                      </span>
                      <div
                        className="w-full flex flex-col-reverse"
                        style={{ height: "5.5rem" }}
                        title={`${mesCurto(p.mes_ref)} — ${tooltip}`}
                        data-testid={`rh-evocusto-col-${p.mes_ref}`}
                      >
                        {compSel === "empilhado" ? (
                          <>
                            <div
                              className="w-full rounded-b bg-indra-turquesa"
                              style={{ height: `${(p.grupos.salario / evoCusto.max) * 100}%` }}
                            />
                            <div
                              className="w-full bg-indra-turquesa/45"
                              style={{ height: `${(p.grupos.beneficios / evoCusto.max) * 100}%` }}
                            />
                            <div
                              className="w-full rounded-t bg-slate-500/70"
                              style={{ height: `${(p.grupos.encargos / evoCusto.max) * 100}%` }}
                            />
                          </>
                        ) : (
                          <div
                            className={"w-full rounded-t " + (atual ? "bg-indra-turquesa" : "bg-indra-turquesa/45")}
                            style={{ height: `${Math.max(4, ((p.comp[compSel] ?? 0) / evoCusto.max) * 100)}%` }}
                          />
                        )}
                      </div>
                      <span className={"text-xs " + (atual ? "text-white font-semibold" : "text-white/50")}>
                        {mesCurto(p.mes_ref)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-white/40 mt-2">
                Empilhado: salário (teal) · benefícios (teal claro) · encargos (cinza) —
                componentes somados; passe o mouse pra ver os 7.
                {evoCusto.mesesComDif.length > 0 && (
                  <span className="text-amber-300/80" data-testid="rh-evocusto-dif-fonte">
                    {" "}{evoCusto.mesesComDif.map((p) => mesCurto(p.mes_ref)).join("/")}: soma dos
                    componentes difere do total da fonte (linhas sem Total Individual na
                    planilha — em conferência c/ RH).
                  </span>
                )}
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
